<template>
  <div id="account-update" v-loading.fullscreen.lock="isLoading" element-loading-background="rgba(256, 256, 256, 0.6)">
    <b-alert id="success-popup" v-model="showSuccess" variant="success" class="alert alert-success" role="alert">
      <div class="container">
        <div class="alert-icon">
          <i class="now-ui-icons ui-2_like"></i>
        </div>
        <strong> {{ $t("message.details_has_been_changed") }} </strong>
        <button type="button" class="close" data-dismiss="alert" aria-label="Close"></button>
      </div>
    </b-alert>

    <b-alert v-model="showError" variant="danger" dismissible>{{
      $t("message.account_update_unsuccessfull")
    }}</b-alert>
    <div class="form-row">
      <div class="form-group col-md-6">
        <ValidationProvider rules="required" v-slot="{ errors }">
          <label>{{ $t("label.first_name") }} :</label>
          <fg-input type="text" v-model.lazy="updateBody.first_name" :placeholder="$t('input.placeholder.first_name')"
            :error="errors[0]"></fg-input>
        </ValidationProvider>
      </div>
      <div class="form-group col-md-6">
        <ValidationProvider rules="required" v-slot="{ errors }">
          <label for="inputLastName">{{ $t("label.last_name") }} :</label>
          <fg-input id="inputLastName" type="text" :error="errors[0]" v-model.lazy="updateBody.last_name"
            :placeholder="$t('input.placeholder.last_name')"></fg-input>
        </ValidationProvider>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group col-md-12">
        <ValidationProvider rules="email|required" v-slot="{ errors }">
          <label>{{ $t("label.email") }}:</label>
          <fg-input :disabled="getProviderName" type="text" v-model.lazy="updateBody.email"
            :placeholder="$t('input.placeholder.email')" :error="errors[0]"></fg-input>
        </ValidationProvider>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group col-md-12">
        <ValidationProvider rules="numeric|required" v-slot="{ errors }">
          <label>{{ $t("label.contact_number") }}</label>
          <fg-input type="tel" v-model="updateBody.telephone_number" :placeholder="$t('input.placeholder.mobile_number')"
            :error="errors[0]" :name="$t('input.placeholder.mobile_number')"></fg-input>
        </ValidationProvider>
      </div>
    </div>
    <div v-if="!getProviderName">
      <div class="form-row">
        <n-checkbox v-model="updatePassword">
          <p class="font-weight-bold">
            {{ $t("label.update_password") }}
          </p>
        </n-checkbox>
      </div>
      <div class="form-row" v-if="updatePassword">
        <div class="form-group col-md-6">
          <ValidationProvider ref="passwordValidator" rules="required|min:8|password:password_confirmation"
            v-slot="{ errors }">
            <label for="password">{{ $t("label.password") }}</label>
            <div class="input-group">
              <fg-input :type="showPassword ? 'text' : 'password'" ref="password" id="password" :error="errors[0]"
                v-model.lazy="updateBody.password" :placeholder="$t('input.placeholder.create_password')"
                style="width: 100%; padding-right: 1px"></fg-input>
              <n-button type="primary" round icon class="add-on-input icon-eye" :style="myTheme.customizeButton"
                @click="showPassword = !showPassword">
                <i id="pass-status" :class="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
              </n-button>
            </div>
          </ValidationProvider>
        </div>
        <div class="form-group col-md-6">
          <ValidationProvider rules="required" name="password_confirmation" v-slot="{ errors }"
            ref="passwordConfirmationValidator">
            <label>{{ $t("label.confirm_password") }}</label>
            <fg-input data-vv-name="password_confirmation" type="password" ref="password_confirmation"
              id="passwordConfirmation" v-model.lazy="updateBody.password_confirmation"
              :placeholder="$t('input.placeholder.confirm_password')" :error="errors[0]"></fg-input>
          </ValidationProvider>
        </div>
      </div>
    </div>
    <n-button :style="myTheme.customizeButton" round @click="updateUser()">{{
      $t("button.update_details")
    }}</n-button>

    <n-button round outline type="secondary" class="pull-right" @click="$emit('onDeleteAccount')">
      <i class="k-icon-trash mr-1"></i>{{ $t("button.delete_account") }}
    </n-button>
  </div>
</template>
<script>
import { FormGroupInput, Button, Checkbox } from "@/components";

import { mapActions, mapGetters } from "vuex";
import { ValidationProvider } from "vee-validate";
import router from "../router";
import objectUser from "@/model/user";
import { showErrorMessageAlert, getMessageFromAPIError } from "@/utilities/helper";
export default {
  name: "update-account",
  props: {
    avatarUpdated: Boolean,
  },
  computed: {
    ...mapGetters('locationStore', [ "getProviderName"]),
  },
  components: {
    [FormGroupInput.name]: FormGroupInput,
    [Button.name]: Button,
    [Checkbox.name]: Checkbox,
    ValidationProvider,
  },
  data() {
    return {
      showSuccess: false,
      showError: false,
      errors: [],
      updateBody: objectUser,
      isLoading: false,
      showPassword: false,
      updatePassword: false,
      passwordValidation: true,
    };
  },
  methods: {
    ...mapActions('userStore', ["fetchAuthUser", "updateUserRequest"]),
    async updateUser() {

      if (this.avatarUpdated) this.updateBody.avatar = this.user.avatar;

      if (!this.updatePassword) {
        delete this.updateBody.password;
        delete this.updateBody.password_confirmation;
      } else {
        await this.$refs.passwordValidator.validate().then((response) => {
          this.passwordValidation = response.valid
        });
      }
      // can't update the password if it's not valid
      if (!this.passwordValidation) return;

      this.isLoading = true;
      this.updateUserRequest(this.updateBody)
        .then((response) => {
          if (response && response.error) {
            showErrorMessageAlert(
              getMessageFromAPIError(response)
            );
            return;
          }

          this.fetchAuthUser().then(() => {
            this.showSuccess = true;
            if (this.$route.query.redirectTo) {
              router.push({ name: this.$route.query.redirectTo })
            }
          });
        })
        .catch((e) => {
          this.captureException(e)
          this.showError = true;
          this.errors.push;
        }).finally(() => {
          this.isLoading = false;
        });
    },
  },
  mounted() {
    if (!this.isAuthenticated) {
      router.push("/login");
    }
    this.updateBody = {
      email: this.user.email,
      first_name: this.user.first_name,
      last_name: this.user.last_name,
      telephone_number: this.user.telephone_number,
      password: null,
      password_confirmation: null,
    };
  },
};
</script>
<style scoped>
.form-group {
  width: 100%;
}

.add-on-input {
  margin-top: 0px !important;
}

.btn-outline-secondary {
  color: #6c757d !important;
  background: #ffffff !important;
  border: 2px solid #6c757d !important;
}
</style>
